import type { GetServerSidePropsContext } from "next";
import type { ReadonlyRequestCookies } from "next/dist/server/web/spec-extension/adapters/request-cookies";

export const EXPERIMENT_FLAGS_COOKIE_NAME = "experiment-flags";
const EXPERIMENT_CONTROL = "0";

type CookieValue = string | Record<string, string>;

export const getExperimentValue = (
  cookies: Partial<{ [key: string]: CookieValue }>,
  flag: string
): string | boolean | number | undefined => {
  const flags = getExperimentValues(cookies);

  if (flags && flag in flags) {
    if (!isNaN(flags[flag] as number)) {
      return Number(flags[flag]);
    }
    return flags[flag];
  }
  return undefined;
};

export const getExperimentValues = (
  cookies: Partial<{ [key: string]: CookieValue }>
): Record<string, number | string | boolean> | undefined => {
  if (!cookies) return undefined;

  try {
    const cookie = cookies[EXPERIMENT_FLAGS_COOKIE_NAME];

    if (!cookie) return undefined;

    if (typeof cookie === "string") {
      return JSON.parse(decodeURIComponent(cookie));
    }

    // N.B: typeof 'object' values need to be stringified to be parsed.
    // This allows for using js-cookie value and interoperability between req objects and client-side.
    return JSON.parse(JSON.stringify(cookie));
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (error: unknown) {
    console.error("Failed to parse experiment-flags cookie value");
  }
  return undefined;
};

export const getExperimentVariantFromRequest = (
  req: GetServerSidePropsContext["req"],
  experimentId?: string | null
): string => {
  try {
    if (!experimentId) {
      return EXPERIMENT_CONTROL;
    }
    const allExperiments = decodeURIComponent(
      req.cookies[EXPERIMENT_FLAGS_COOKIE_NAME] ?? "{}"
    );
    const parsedExperiments = JSON.parse(allExperiments);

    return parsedExperiments[experimentId] ?? EXPERIMENT_CONTROL;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (err: unknown) {
    return EXPERIMENT_CONTROL;
  }
};

export const getExperimentVariantFromQueryString = (
  query: GetServerSidePropsContext["query"]
): string => {
  const selectedVariant = query?.selectedVariant;
  if (typeof selectedVariant !== "string") {
    return EXPERIMENT_CONTROL;
  }
  return selectedVariant;
};

export const getExperimentFlags = (cookieStore: ReadonlyRequestCookies) => {
  let experimentFlags = {};
  try {
    // explicitly decode uri encoded cookie
    const experimentFlagsValue = decodeURIComponent(
      cookieStore.get(EXPERIMENT_FLAGS_COOKIE_NAME)?.value ?? "{}"
    );
    experimentFlags = JSON.parse(experimentFlagsValue);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (e) {
    console.error("Failed to parse experiment-flags cookie value");
  }

  return experimentFlags;
};
